import { useState, useEffect, useCallback, useMemo, useRef, useTransition } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useChat } from '../contexts/ChatContext';
import { useModel } from '../contexts/ModelContext';
import { useKnowledgeBase } from '../contexts/KnowledgeBaseContext';
import { useLiterature } from '../contexts/LiteratureContext';
import { chatAPI } from '../services/api/chatApi';
import { researchAPI } from '../services/api/researchApi';
import * as chatStorage from '../storage/chatStorage';
import type { AIModel } from '../types/models';
import { TurnId, CreateTurnArgs, CreateTurnResult, ConversationState } from '../types/conversation';
import { MessageRequest } from '../schemas/chat';
import { toConversationView } from '../utils/conversationAdapter';
import { LiteratureOptions } from '../schemas/chat';

// Re-export chatAPI for use in this file
export { chatAPI };

// ============================================================================
// Constants
// ============================================================================

const MAX_TITLE_LENGTH = 50;
const TITLE_SUFFIX = '...';
const DEFAULT_MODEL_ID = '1';
const NAVIGATION_REPLACE_DELAY = 0;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a conversation title from content
 * Truncates to MAX_TITLE_LENGTH if necessary
 */
function generateTitle(content: string): string {
  const trimmed = content.trim();
  if (trimmed.length <= MAX_TITLE_LENGTH) return trimmed;
  return trimmed.substring(0, MAX_TITLE_LENGTH) + TITLE_SUFFIX;
}

/**
 * Get default model ID from available models
 * Returns selected model, conversation model, first available model, or default
 */
function getDefaultModelId(
  selectedModel: string | null,
  conversationModel: string | undefined,
  models: AIModel[]
): string {
  return (
    selectedModel || conversationModel || (models.length > 0 ? models[0].id : DEFAULT_MODEL_ID)
  );
}

/**
 * Generate a unique Turn ID using timestamp and random string
 */
function generateTurnId(): TurnId {
  return `turn-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Check if conversation has meaningful updates
 * Compares turns length, current turn ID, answer text, status, and thinking content
 */
function hasConversationUpdate(
  currentView: {
    turns: { length: number };
    currentTurn: { id?: string; answerText?: string; status?: string; thinking?: string } | null;
  },
  newConversation: {
    turns: { length: number };
    currentTurn: { id?: string; answerText?: string; status?: string; thinking?: string } | null;
  }
): boolean {
  return (
    currentView.turns.length !== newConversation.turns.length ||
    (newConversation.turns.length > 0 &&
      currentView.turns.length > 0 &&
      currentView.currentTurn?.id !== newConversation.currentTurn?.id) ||
    (newConversation.turns.length > 0 &&
      currentView.turns.length > 0 &&
      currentView.currentTurn?.answerText !== newConversation.currentTurn?.answerText) ||
    (newConversation.turns.length > 0 &&
      currentView.turns.length > 0 &&
      currentView.currentTurn?.status !== newConversation.currentTurn?.status) ||
    (newConversation.turns.length > 0 &&
      currentView.turns.length > 0 &&
      currentView.currentTurn?.thinking !== newConversation.currentTurn?.thinking)
  );
}

// ============================================================================
// Custom Hooks - Separation of Concerns
// ============================================================================

/**
 * Hook to manage conversation initialization logic
 * Handles loading existing conversations or creating new ones
 *
 * @param conversationId - ID of conversation to load, or undefined for new
 * @param messageRequest - Initial message for new conversation
 * @param onLoad - Callback to load existing conversation
 * @param onCreate - Callback to create new conversation
 */
function useConversationInitializer(
  conversationId: string | undefined,
  messageRequest: MessageRequest | null,
  onLoad: (id: string) => Promise<void>,
  onCreate: (request: MessageRequest) => Promise<void>
) {
  // Track initialization state to prevent duplicate calls
  const initializedRef = useRef<{
    conversationId: string | undefined;
    messageContent: string | undefined;
  }>({ conversationId: undefined, messageContent: undefined });

  useEffect(() => {
    let aborted = false;
    let mounted = true;

    // Check if we've already initialized this exact conversation
    const messageContent = messageRequest?.content;
    const alreadyInitialized =
      initializedRef.current.conversationId === conversationId &&
      initializedRef.current.messageContent === messageContent;

    if (alreadyInitialized) {
      return;
    }

    // Mark as initialized before async operations
    initializedRef.current = { conversationId, messageContent };

    const initialize = async () => {
      if (!mounted || aborted) return;

      if (conversationId) {
        await onLoad(conversationId);
      } else if (messageRequest?.content?.trim()) {
        if (aborted) return;
        await onCreate(messageRequest);
      }
    };

    initialize();

    return () => {
      aborted = true;
      mounted = false;
    };
  }, [conversationId, messageRequest, onLoad, onCreate]);
}

/**
 * Hook to manage recommended questions fetching
 * Fetches suggested actions when a turn completes
 * Questions are isolated per conversation ID
 *
 * @param conversationId - Current conversation ID
 * @param conversation - Current conversation state
 * @returns Array of suggested question strings
 */
function useRecommendedQuestions(
  conversationId: string | undefined,
  conversation: ConversationState
): { actions: string[]; isLoading: boolean } {
  const [suggestedActions, setSuggestedActions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastProcessedTurnRef = useRef<TurnId | null>(null);
  const isFetchingRef = useRef(false);
  const lastConversationIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Clear questions when switching to a different conversation
    if (conversationId !== lastConversationIdRef.current) {
      lastConversationIdRef.current = conversationId;
      lastProcessedTurnRef.current = null;
      isFetchingRef.current = false;
      setSuggestedActions([]);
      setIsLoading(false);
      return;
    }

    // Reset when no conversation
    if (!conversationId) {
      lastProcessedTurnRef.current = null;
      setSuggestedActions([]);
      setIsLoading(false);
      return;
    }

    // Guard clauses
    if (
      conversation.type !== 'ready' ||
      !conversation.conversation.currentTurn ||
      conversation.conversation.currentTurn.status !== 'completed'
    ) {
      return;
    }

    const currentTurn = conversation.conversation.currentTurn;

    // Only fetch once per turn
    if (lastProcessedTurnRef.current === currentTurn.id || isFetchingRef.current) {
      return;
    }

    console.log(
      `[useRecommendedQuestions] Fetching questions for conversation ${conversationId}, turn ${currentTurn.id}`
    );
    lastProcessedTurnRef.current = currentTurn.id;
    isFetchingRef.current = true;
    setIsLoading(true);

    researchAPI
      .getChatRecommendedQuestions(conversation.conversation.id)
      .then(response => {
        const questions = response.data.questions.map((q: { question: string }) => q.question);
        console.log(`[useRecommendedQuestions] Got ${questions.length} questions`);
        setSuggestedActions(questions);
      })
      .catch(error => {
        console.error('Failed to fetch recommended questions:', error);
        setSuggestedActions([]);
        lastProcessedTurnRef.current = null;
      })
      .finally(() => {
        isFetchingRef.current = false;
        setIsLoading(false);
      });
  }, [conversationId, conversation]);

  return { actions: suggestedActions, isLoading };
}

/**
 * Hook to manage location state cleanup
 * Clears navigation state after processing
 *
 * @param conversationId - Current conversation ID
 */
function useLocationStateCleanup(conversationId: string | undefined) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (conversationId && location.state) {
      const timer = setTimeout(() => {
        navigate(`/chat/${conversationId}`, { replace: true, state: null });
      }, NAVIGATION_REPLACE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [conversationId, location.state, navigate]);
}

/**
 * Hook to manage streaming state and cancellation
 * Provides methods to start, cancel, and end streaming operations
 *
 * @returns Object with streaming control methods
 */
function useStreamingControl() {
  const streamingTurnIdRef = useRef<TurnId | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const startStreaming = useCallback((turnId: TurnId): AbortController => {
    // Clean up previous streaming if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    streamingTurnIdRef.current = turnId;
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  }, []);

  const cancelStreaming = useCallback((turnId: TurnId): boolean => {
    if (streamingTurnIdRef.current === turnId && abortControllerRef.current) {
      abortControllerRef.current.abort();
      streamingTurnIdRef.current = null;
      abortControllerRef.current = null;
      return true;
    }
    return false;
  }, []);

  const endStreaming = useCallback((turnId: TurnId) => {
    if (streamingTurnIdRef.current === turnId) {
      streamingTurnIdRef.current = null;
      abortControllerRef.current = null;
    }
  }, []);

  const isStreaming = useCallback((turnId: TurnId): boolean => {
    return streamingTurnIdRef.current === turnId;
  }, []);

  return { startStreaming, cancelStreaming, endStreaming, isStreaming };
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Custom hook to manage conversation state and operations
 * Based on the Turn abstraction: Turn is a complete, immutable version of
 * "user intent → AI response"
 *
 * @returns Object containing conversation state, actions, and metadata
 *
 * Best practices applied:
 * - Separated concerns into focused sub-hooks
 * - Removed unnecessary ref syncing patterns
 * - Simplified dependency arrays with useCallback and useMemo
 * - Improved cleanup and abort handling
 * - Clear separation between state and side effects
 * - Proper TypeScript typing throughout
 * - Extracted pure utility functions
 * - Performance optimizations with memoization
 * - Concurrent mode support with useTransition
 */
export function useConversation() {
  // Support for concurrent updates
  const [isPending, startTransition] = useTransition();

  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    state: chatState,
    createConversation,
    updateConversationTitle,
    loadConversationWithMessages,
    sendMessageStream,
  } = useChat();
  const { selectedModel, setSelectedModel, models } = useModel();
  const {
    state: kbState,
    toggleKnowledgeBase,
    selectAllKnowledgeBases,
    clearSelectedKnowledgeBases,
    setSelectedKnowledgeBases,
  } = useKnowledgeBase();
  const { state: literatureState, setSelectedSources, setOptions } = useLiterature();

  // ============================================================================
  // Core State
  // ============================================================================
  const [state, setState] = useState<ConversationState>({ type: 'loading' });

  // ============================================================================
  // Memoized Derived Values
  // ============================================================================

  /**
   * Extract message request from location state
   * Memoized to avoid unnecessary re-renders
   */
  const locationMessageRequest = useMemo<MessageRequest | null>(() => {
    const state = location.state as { messageRequest?: MessageRequest } | null;
    return state?.messageRequest || null;
  }, [location.state]);

  /**
   * Current conversation view from context
   * Recomputes only when conversations or conversationId changes
   * Returns conversation if it exists and has been loaded with messages
   */
  const conversation = useMemo(() => {
    if (!conversationId) {
      console.log(`[useConversation] conversation memo: NO conversationId`);
      return null;
    }
    const conv = chatState.conversations.find(c => c.id === conversationId);
    // Return null if conversation doesn't exist
    if (!conv) {
      console.log(
        `[useConversation] conversation memo: conversation ${conversationId} NOT FOUND in ${chatState.conversations.length} conversations`
      );
      return null;
    }
    // For new conversations, messages might be undefined - treat as empty array
    // This allows the conversation view to be created and synced properly
    const convWithMessages = conv.messages ? conv : { ...conv, messages: [] };
    const view = toConversationView(convWithMessages);
    console.log(`[useConversation] ✅ conversation memo recalculated`, {
      conversationId,
      messagesCount: convWithMessages.messages?.length,
      turnsCount: view.turns.length,
      currentTurnStatus: view.currentTurn?.status,
      currentTurnHasContent: (view.currentTurn?.answerText || '').length > 0,
    });
    return view;
  }, [chatState.conversations, conversationId]);

  /**
   * Array of turns for easy access
   * Memoized to prevent unnecessary re-renders
   */
  const turns = useMemo(() => {
    return state.type === 'ready' ? state.conversation.turns : [];
  }, [state]);

  // ============================================================================
  // Streaming Control
  // ============================================================================
  const { startStreaming, cancelStreaming, endStreaming } = useStreamingControl();

  // ============================================================================
  // Action Handlers
  // ============================================================================

  /**
   * Load an existing conversation
   * Skips backend fetch if conversation is already in memory with active streaming
   */
  const handleLoadConversation = useCallback(
    async (id: string) => {
      // Check if we have active streaming messages in context for this conversation
      const existingConv = chatState.conversations.find(c => c.id === id);
      const hasActiveStreaming = existingConv?.messages?.some(
        m => m.role === 'assistant' && !(m.is_done ?? true)
      );

      if (hasActiveStreaming) {
        // Don't fetch from API - preserve the streaming state
        return;
      }

      // Always load from cache/API - don't skip even if conversation exists
      // This ensures fresh data on page refresh and navigation

      // Try loading from cache first for instant display (optimistic)
      const cachedMessages = await chatStorage.getMessages(id);
      const hasIncompleteStreaming = cachedMessages?.some(
        m => m.role === 'assistant' && !(m.is_done ?? true)
      );

      if (hasIncompleteStreaming) {
        // If cache has incomplete streaming messages, clear the cache
        // The streaming was interrupted by refresh, so we need to fetch from API
        await chatStorage.deleteMessages(id);
        // Show loading state while fetching from API
        setState({ type: 'loading' });
      } else if (cachedMessages && cachedMessages.length > 0) {
        console.log(
          `[useConversation] Loaded ${cachedMessages.length} messages from cache for ${id}`
        );
        // Create minimal conversation structure with cached messages
        const convWithCache = {
          id,
          title: 'Chat',
          messages: cachedMessages,
          model_id: null,
          model_name: null,
        } as any;
        const view = toConversationView(convWithCache);
        setState({ type: 'ready', conversation: view });
      } else {
        // No cache available, show loading state
        setState({ type: 'loading' });
      }

      // Always sync with API (will overwrite cache)
      try {
        const conv = await loadConversationWithMessages(id);
        if (conv) {
          const view = toConversationView(conv);
          setState({ type: 'ready', conversation: view });
        } else {
          navigate('/', { replace: true });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load conversation';
        setState({ type: 'error', message });
      }
    },
    [loadConversationWithMessages, navigate, chatState.conversations]
  );

  /**
   * Create a new conversation with initial message
   */
  const handleCreateConversation = useCallback(
    async (messageRequest: MessageRequest) => {
      console.log(`[useConversation] handleCreateConversation: START`, {
        content: messageRequest.content,
      });
      setState({ type: 'loading' });
      try {
        const title = generateTitle(messageRequest.content);

        const conv = await createConversation({
          title: title,
          model_id: messageRequest.model_id,
          description: messageRequest.description,
          knowledge_base_ids: messageRequest.knowledge_base_ids,
          literature_sources: messageRequest.literature_sources,
          literature_options: messageRequest.literature_options || undefined,
        });
        if (!conv) {
          setState({ type: 'error', message: 'Failed to create conversation' });
          return;
        }

        console.log(`[useConversation] handleCreateConversation: conversation created`, {
          id: conv.id,
          hasMessages: !!conv.messages,
          messagesLength: conv.messages?.length,
        });

        // Navigate to the new conversation
        navigate(`/chat/${conv.id}`, { replace: true, state: null });

        // Update title using extracted utility
        updateConversationTitle(conv.id, title);

        // Send initial message
        const modelId = getDefaultModelId(selectedModel, conv.model_id || undefined, models);
        // Backend expects file IDs as strings, but we need metadata for optimistic display
        // We'll pass the full metadata through - chatService will extract IDs for the API
        const fileMetadata =
          messageRequest.files?.map(f => ({
            file_id: f.id,
            file_name: f.original_name || f.name || 'Unknown',
            file_type: f.file_type || 'document',
          })) || [];

        console.log(`[useConversation] handleCreateConversation: calling sendMessageStream`);
        await sendMessageStream(conv.id, {
          content: messageRequest.content,
          model_id: modelId,
          knowledge_base_ids: messageRequest.knowledge_base_ids,
          files: fileMetadata, // Send metadata for optimistic update
          stream: true,
          literature_sources: messageRequest.literature_sources,
          literature_options: messageRequest.literature_options,
        });
        console.log(`[useConversation] handleCreateConversation: sendMessageStream completed`);
      } catch (error) {
        console.error(`[useConversation] handleCreateConversation: ERROR`, error);
        setState({
          type: 'error',
          message: 'Failed to create conversation',
        });
      }
    },
    [
      createConversation,
      navigate,
      updateConversationTitle,
      selectedModel,
      models,
      sendMessageStream,
    ]
  );

  // ============================================================================
  // Side Effects
  // ============================================================================

  /**
   * Sync conversation state from context
   * Uses transition for non-urgent state updates
   */
  useEffect(() => {
    console.log(`[useConversation] State sync useEffect triggered`, {
      stateType: state.type,
      hasConversation: !!conversation,
      conversationId: conversation?.id,
    });

    if (state.type === 'ready' && conversation) {
      const currentView = state.conversation;

      if (hasConversationUpdate(currentView, conversation)) {
        const isStreaming = conversation.currentTurn?.status === 'streaming';

        // Use transition for non-streaming updates, but update immediately for streaming
        // to show incremental text updates as they arrive
        console.log(`[useConversation] State sync: UPDATING conversation`, {
          currentStatus: currentView.currentTurn?.status,
          newStatus: conversation.currentTurn?.status,
          isStreaming,
        });

        if (isStreaming) {
          // Don't use transition for streaming - need immediate updates
          setState({ type: 'ready', conversation });
        } else {
          // Use transition for completed turns (non-urgent)
          startTransition(() => {
            setState({ type: 'ready', conversation });
          });
        }
      } else {
        console.log(`[useConversation] State sync: no update needed`);
      }
    } else if (state.type === 'loading' && conversation) {
      console.log(`[useConversation] State sync: transitioning from loading to ready`);
      // Sync knowledge base and literature state from conversation on initial load only
      // This ensures user's manual selections during chat are not overridden
      if (conversation.knowledgeBaseIds) {
        setSelectedKnowledgeBases(conversation.knowledgeBaseIds);
      }
      if (conversation.literatureSources) {
        setSelectedSources(conversation.literatureSources);
      }
      if (conversation.literatureOptions) {
        setOptions(conversation.literatureOptions);
      }
      setState({ type: 'ready', conversation });
    } else {
      console.log(`[useConversation] State sync: skipped`, {
        reason:
          state.type === 'loading'
            ? 'state is loading but no conversation yet'
            : 'state is ready but no conversation yet',
      });
    }
    // Only depend on conversation - state is used as comparison target only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, startTransition, setSelectedKnowledgeBases]);

  /**
   * Initialize conversation on mount or when params change
   */
  useConversationInitializer(
    conversationId,
    locationMessageRequest,
    handleLoadConversation,
    handleCreateConversation
  );

  /**
   * Clean up location state after processing
   */
  useLocationStateCleanup(conversationId);

  /**
   * Load recommended questions
   */
  const suggestedActions = useRecommendedQuestions(conversationId, state);

  /**
   * Redirect to home if no conversation
   */
  useEffect(() => {
    if (!conversationId && !locationMessageRequest) {
      navigate('/', { replace: true });
    }
  }, [conversationId, locationMessageRequest, navigate]);

  // ============================================================================
  // Core API: createTurn
  // ============================================================================

  /**
   * Create a new turn in the conversation
   * @param args - Turn creation arguments
   * @returns Promise with turn ID
   */
  const createTurn = useCallback(
    async (args: CreateTurnArgs): Promise<CreateTurnResult> => {
      if (state.type !== 'ready') {
        throw new Error('Conversation not ready');
      }

      const view = state.conversation;
      const isNewConversation = view.turns.length === 0;

      // Update title for new conversation
      if (isNewConversation && args.content.text.trim()) {
        const title = generateTitle(args.content.text);
        updateConversationTitle(view.id, title);
      }

      // Generate Turn ID and start streaming
      const turnId = generateTurnId();
      const abortController = startStreaming(turnId);

      try {
        // Determine model to use
        const modelId = getDefaultModelId(args.modelRef || selectedModel, view.model, models);

        // Use args literature settings if provided, otherwise use state
        const turnLiteratureSources = args.literature_sources || literatureState.selectedSources;
        const turnLiteratureOptions = args.literature_options || literatureState.options;
        const knowledgeBaseIds = Array.from(kbState.selectedKnowledgeBases);

        // Send message stream
        await sendMessageStream(
          view.id,
          {
            content: args.content.text,
            model_id: modelId,
            knowledge_base_ids: knowledgeBaseIds.length > 0 ? knowledgeBaseIds : undefined,
            files: args.content.attachments || [],
            stream: true,
            literature_sources:
              turnLiteratureSources.length > 0 ? turnLiteratureSources : undefined,
            literature_options:
              turnLiteratureSources.length > 0 ? turnLiteratureOptions : undefined,
          },
          undefined,
          undefined
        );

        return { turnId };
      } catch (error) {
        // Don't throw if aborted
        if (abortController.signal.aborted) {
          return { turnId };
        }
        console.error('Failed to create turn:', error);
        throw error;
      } finally {
        endStreaming(turnId);
      }
    },
    [
      state,
      selectedModel,
      models,
      updateConversationTitle,
      sendMessageStream,
      startStreaming,
      endStreaming,
      kbState.selectedKnowledgeBases,
      literatureState.options,
      literatureState.selectedSources,
    ]
  );

  // ============================================================================
  // Core API: cancel
  // ============================================================================

  /**
   * Cancel an ongoing streaming turn
   * @param turnId - ID of turn to cancel
   */
  const cancel = useCallback(
    (turnId: TurnId) => {
      cancelStreaming(turnId);
    },
    [cancelStreaming]
  );

  // ============================================================================
  // Selection Methods
  // ============================================================================

  const selectModel = useCallback(
    (modelId: string) => {
      setSelectedModel(modelId);
    },
    [setSelectedModel]
  );

  const selectKnowledgebase = useCallback(
    (kbId: string) => {
      toggleKnowledgeBase(kbId);
    },
    [toggleKnowledgeBase]
  );

  const selectLiterature = useCallback(
    (sources: string[]) => {
      setSelectedSources(sources);
    },
    [setSelectedSources]
  );

  const selectLiteratureOptions = useCallback(
    (options: LiteratureOptions) => {
      setOptions(options);
    },
    [setOptions]
  );

  const selectAllKnowledgebases = useCallback(() => {
    selectAllKnowledgeBases();
  }, [selectAllKnowledgeBases]);

  const clearAllKnowledgebases = useCallback(() => {
    clearSelectedKnowledgeBases();
  }, [clearSelectedKnowledgeBases]);

  // ============================================================================
  // Return API
  // ============================================================================

  return {
    // State
    state,
    conversation: state.type === 'ready' ? state.conversation : null,
    turns,
    isPending, // Concurrent mode pending state

    // ChatContext state (inline)
    chatError: chatState.error,
    chatLoading: chatState.isLoading,

    // Recommended questions
    suggestedActions: suggestedActions.actions,
    suggestedActionsLoading: suggestedActions.isLoading,

    // Core Turn API
    createTurn,
    cancel,

    // Model-related
    selectedModel,
    selectModel,
    models,

    // Knowledge Base state and methods
    kbState,
    selectKnowledgebase,
    selectAllKnowledgebases,
    clearAllKnowledgebases,

    // Literature state and methods
    literatureSources: literatureState.selectedSources,
    selectLiterature,
    literatureOptions: literatureState.options,
    selectLiteratureOptions,
  };
}
